import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Key, UserMinus, UserPlus, Trash } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DotsThreeVertical } from "@phosphor-icons/react";

const ROLES = ["admin","production_manager","supervisor","operator","qc","store","purchase","sales","dispatch"];
const DEPARTMENTS = ["Preparation","Cooking","Loading","Packing","Dispatch","QC","Admin"];
const SHIFTS = ["day","night","general"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "operator" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); setForm({email:"",password:"",name:"",role:"operator"}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, email: u.email, role: u.role, phone: u.phone||"", department: u.department||"", shift: u.shift||"day", status: u.status||"active" });
  };
  const saveEdit = async () => {
    try { await api.put(`/users/${editUser.id}`, editForm); toast.success("User updated successfully."); setEditUser(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const resetPwd = async () => {
    if (newPwd.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post(`/users/${pwdUser.id}/reset-password`, { password: newPwd });
          toast.success("Password reset successfully.");
          setPwdUser(null); setNewPwd(""); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const toggleStatus = async (u) => {
    const isActive = (u.status || "active") === "active";
    try {
      await api.post(`/users/${u.id}/${isActive ? "deactivate" : "activate"}`);
      toast.success(`User ${u.email} ${isActive ? "deactivated" : "reactivated"}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader subtitle="Access Control" title="Users & Roles"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus size={16} className="mr-1.5"/>Add User</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create User</DialogTitle><DialogDescription className="sr-only">Create User</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
                <div><Label>Email</Label><Input value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
                <div><Label>Password</Label><Input type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})}/></div>
                <div className="col-span-2"><Label>Role</Label>
                  <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{ROLES.map(r=><SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="user-save">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead>
              <TableHead>Role</TableHead><TableHead>Department</TableHead>
              <TableHead>Shift</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map(u=>{
                const active = (u.status || "active") === "active";
                return (
                <TableRow key={u.id} className={!active ? "opacity-70" : ""} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-sm mono">{u.email}</TableCell>
                  <TableCell><Badge variant="secondary" className="uppercase text-[10px]">{u.role?.replace("_"," ")}</Badge></TableCell>
                  <TableCell className="text-xs">{u.department || "—"}</TableCell>
                  <TableCell className="text-xs">{u.shift || "—"}</TableCell>
                  <TableCell>{active
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={()=>openEdit(u)}
                            title="Edit" data-testid={`user-edit-${u.email}`}><PencilSimple size={15}/></Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`user-actions-${u.email}`}>
                          <DotsThreeVertical size={16} weight="bold"/>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={()=>setPwdUser(u)} data-testid={`user-resetpw-${u.email}`}>
                          <Key size={14} className="mr-2"/>Reset password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem onSelect={()=>toggleStatus(u)}
                                          className={active ? "text-amber-700" : "text-emerald-700"}
                                          data-testid={`user-toggle-${u.email}`}>
                          {active ? <><UserMinus size={14} className="mr-2"/>Deactivate</> : <><UserPlus size={14} className="mr-2"/>Reactivate</>}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>);
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={v=>!v && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit user · {editUser?.name}</DialogTitle>
            <DialogDescription>Update user profile and role. Email must be unique.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={editForm.name||""} onChange={e=>setEditForm({...editForm, name:e.target.value})} data-testid="user-edit-name"/></div>
            <div className="col-span-2"><Label>Email</Label><Input value={editForm.email||""} onChange={e=>setEditForm({...editForm, email:e.target.value})} data-testid="user-edit-email"/></div>
            <div><Label>Phone</Label><Input value={editForm.phone||""} onChange={e=>setEditForm({...editForm, phone:e.target.value})} data-testid="user-edit-phone"/></div>
            <div><Label>Role</Label>
              <Select value={editForm.role} onValueChange={v=>setEditForm({...editForm, role:v})}>
                <SelectTrigger data-testid="user-edit-role"><SelectValue/></SelectTrigger>
                <SelectContent>{ROLES.map(r=><SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Department</Label>
              <Select value={editForm.department||""} onValueChange={v=>setEditForm({...editForm, department:v})}>
                <SelectTrigger data-testid="user-edit-dept"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Shift</Label>
              <Select value={editForm.shift||"day"} onValueChange={v=>setEditForm({...editForm, shift:v})}>
                <SelectTrigger data-testid="user-edit-shift"><SelectValue/></SelectTrigger>
                <SelectContent>{SHIFTS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="user-save-edit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdUser} onOpenChange={v=>!v && (setPwdUser(null), setNewPwd(""))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password · {pwdUser?.name}</DialogTitle>
            <DialogDescription>The user should change this after next login. Minimum 6 characters.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>New password</Label>
            <Input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)}
                   autoFocus data-testid="user-newpw"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPwdUser(null)}>Cancel</Button>
            <Button onClick={resetPwd} data-testid="user-resetpw-confirm">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
